import { useEffect, useState } from 'react';

/**
 * useDevice — detecção combinada de dispositivo + capacidades.
 *
 * Combina UA parsing, feature detection e media queries pra dar uma visão
 * precisa de:
 *  - Plataforma (iOS / Android / Desktop)
 *  - Tamanho de tela (categoria, dimensões, orientação)
 *  - Capacidades (touch, motion preferences, standalone PWA)
 *  - Especificidades visuais do iOS (notch, dynamic island, status bar)
 *
 * Atualiza dinamicamente em mudanças de viewport/orientação. SSR-safe (retorna
 * defaults razoáveis no servidor).
 *
 * Use pra:
 *  - Esconder/mostrar features condicionalmente (instalação PWA só pra iOS)
 *  - Ajustar tamanhos de fonte/padding em telas pequenas (iPhone SE)
 *  - Adaptar layout em foldables/tablets
 *  - Microinterações que dependem de pointer fine vs coarse
 */

export type DeviceSizeCategory = 'small' | 'medium' | 'large' | 'tablet' | 'desktop';

export interface DeviceInfo {
  // Plataforma
  isIOS: boolean;
  isAndroid: boolean;
  isMobile: boolean; // qualquer touch device
  isDesktop: boolean;
  isPWAInstalled: boolean; // rodando como app standalone

  // iOS-specifics — heurística por screen size do iPhone com notch/island
  hasNotch: boolean; // iPhone X+ (incluindo dynamic island)
  hasDynamicIsland: boolean; // iPhone 14 Pro+

  // Tela
  screenWidth: number;
  screenHeight: number;
  orientation: 'portrait' | 'landscape';
  sizeCategory: DeviceSizeCategory;

  // Input / capacidades
  isCoarsePointer: boolean; // touchscreen
  isFinePointer: boolean; // mouse
  prefersReducedMotion: boolean;
}

const DEFAULT: DeviceInfo = {
  isIOS: false,
  isAndroid: false,
  isMobile: false,
  isDesktop: true,
  isPWAInstalled: false,
  hasNotch: false,
  hasDynamicIsland: false,
  screenWidth: 1024,
  screenHeight: 768,
  orientation: 'portrait',
  sizeCategory: 'desktop',
  isCoarsePointer: false,
  isFinePointer: true,
  prefersReducedMotion: false,
};

/**
 * Categoriza por largura da viewport. Limites escolhidos pra cobrir os
 * dispositivos mais comuns no Brasil:
 *  - small:   ≤ 360px (iPhone SE 1, Android pequenos)
 *  - medium:  361-414px (iPhone 14, Galaxy S, Pixel)
 *  - large:   415-767px (iPhone Pro Max, foldables fechados)
 *  - tablet:  768-1023px (iPad portrait)
 *  - desktop: ≥ 1024px
 */
function getSizeCategory(width: number): DeviceSizeCategory {
  if (width <= 360) return 'small';
  if (width <= 414) return 'medium';
  if (width <= 767) return 'large';
  if (width <= 1023) return 'tablet';
  return 'desktop';
}

/**
 * Heurística pra detectar notch/dynamic island.
 * Não há API pra isso — usamos screen dimensions conhecidas + verificação
 * de `safe-area-inset-top` via CSS env() lido em runtime.
 */
function detectIOSScreenFeatures(width: number, height: number) {
  // iPhones SEM notch: SE (1, 2, 3), 6/7/8 — largura ≤ 375 e altura ≤ 667
  // iPhones COM notch (X até 13): largura 375 (mini) / 390 / 428 e altura 812/844/896/926
  // iPhones COM dynamic island (14 Pro+): largura 393 / 430 e altura 852/932

  const longSide = Math.max(width, height);
  const shortSide = Math.min(width, height);

  // Sem notch: telas curtas (8/SE)
  if (longSide < 812) return { hasNotch: false, hasDynamicIsland: false };

  // Dynamic island: dimensões específicas do 14 Pro / 15 / 16 series
  // 393×852 (14/15 Pro), 430×932 (14/15 Pro Max), 402×874 (16 Pro), 440×956 (16 Pro Max)
  const dynamicIslandSizes = [
    { w: 393, h: 852 },
    { w: 430, h: 932 },
    { w: 402, h: 874 },
    { w: 440, h: 956 },
  ];
  const matchesDynamicIsland = dynamicIslandSizes.some(
    (size) =>
      (shortSide === size.w && longSide === size.h) ||
      (shortSide === size.h && longSide === size.w),
  );

  return {
    hasNotch: longSide >= 812,
    hasDynamicIsland: matchesDynamicIsland,
  };
}

function computeDeviceInfo(): DeviceInfo {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return DEFAULT;

  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && 'ontouchend' in document);
  const isAndroid = /Android/i.test(ua);
  const isMobile = isIOS || isAndroid || /Mobile/i.test(ua);
  const isDesktop = !isMobile;

  // PWA standalone — verifica display-mode E o safari-specific `standalone`
  const isPWAInstalled =
    (typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches) ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;

  const screenWidth = window.innerWidth || window.screen.width;
  const screenHeight = window.innerHeight || window.screen.height;
  const orientation: 'portrait' | 'landscape' =
    screenHeight >= screenWidth ? 'portrait' : 'landscape';

  const iosFeatures = isIOS
    ? detectIOSScreenFeatures(window.screen.width, window.screen.height)
    : { hasNotch: false, hasDynamicIsland: false };

  const isCoarsePointer =
    typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
  const isFinePointer =
    typeof window.matchMedia === 'function' && window.matchMedia('(pointer: fine)').matches;
  const prefersReducedMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return {
    isIOS,
    isAndroid,
    isMobile,
    isDesktop,
    isPWAInstalled,
    hasNotch: iosFeatures.hasNotch,
    hasDynamicIsland: iosFeatures.hasDynamicIsland,
    screenWidth,
    screenHeight,
    orientation,
    sizeCategory: getSizeCategory(screenWidth),
    isCoarsePointer,
    isFinePointer,
    prefersReducedMotion,
  };
}

export function useDevice(): DeviceInfo {
  const [info, setInfo] = useState<DeviceInfo>(() => computeDeviceInfo());

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let raf = 0;
    const update = () => {
      // debounce com rAF — múltiplos resize events numa transição (rotate, etc)
      // recalculam só 1 vez por frame.
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        setInfo(computeDeviceInfo());
      });
    };

    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);

    // Listeners de media queries pra capabilities que podem mudar (display-mode
    // muda quando o usuário instala/desinstala a PWA enquanto rodando)
    const standaloneMq = window.matchMedia('(display-mode: standalone)');
    const motionMq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const pointerMq = window.matchMedia('(pointer: coarse)');

    const mqUpdate = () => update();
    standaloneMq.addEventListener?.('change', mqUpdate);
    motionMq.addEventListener?.('change', mqUpdate);
    pointerMq.addEventListener?.('change', mqUpdate);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      standaloneMq.removeEventListener?.('change', mqUpdate);
      motionMq.removeEventListener?.('change', mqUpdate);
      pointerMq.removeEventListener?.('change', mqUpdate);
    };
  }, []);

  return info;
}

/**
 * Helper pra aplicar atributos data-* no `document.documentElement` baseado no
 * device. Permite estilizar via CSS sem precisar de props React:
 *
 *   [data-device-size="small"] .meu-component { padding: 0.5rem; }
 *   [data-pwa="true"] .meu-component { padding-top: env(safe-area-inset-top); }
 */
export function useDeviceDataAttributes() {
  const device = useDevice();
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.dataset.deviceSize = device.sizeCategory;
    root.dataset.platform = device.isIOS ? 'ios' : device.isAndroid ? 'android' : 'desktop';
    root.dataset.pwa = device.isPWAInstalled ? 'true' : 'false';
    root.dataset.pointer = device.isCoarsePointer ? 'coarse' : 'fine';
    if (device.hasDynamicIsland) root.dataset.island = 'true';
    else delete root.dataset.island;
    if (device.hasNotch) root.dataset.notch = 'true';
    else delete root.dataset.notch;
  }, [device]);
  return device;
}
